import React, { useState, useEffect } from 'react';
import {
  Container,
  VStack,
  HStack,
  Box,
  Heading,
  Text,
  Avatar,
  Badge,
  Flex,
  Grid,
  GridItem,
  Card,
  CardBody,
  Button,
  useToast,
  Input,
  InputGroup,
  InputLeftElement,
  Divider,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import {
  FaSearch,
  FaEnvelope,
  FaEllipsisV,
  FaTrash,
  FaUserCircle,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import MessageInterface from '../components/MessageInterface';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorDisplay from '../components/ErrorDisplay';
import { Message, Post, User } from '../types';
import { 
  mockMessages, 
  mockPosts, 
  mockUsers, 
  getUserById, 
} from '../data/mockData';

interface Conversation {
  id: string;
  participantId: string;
  participant: User;
  postId: string;
  post: Post;
  lastMessage: Message;
  unreadCount: number;
  messages: Message[];
}

const MessagesPage: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentUser] = useState<User>(mockUsers[0]); // Mock current user

  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const loadConversations = async () => {
      setLoading(true);
      try {
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 1200));

        // Group messages into conversations
        const conversationMap = new Map<string, Conversation>();

        mockMessages.forEach(message => {
          // Determine if current user is sender or receiver
          const isCurrentUserSender = message.senderId === currentUser.id;
          const participantId = isCurrentUserSender ? message.receiverId : message.senderId;
          
          const conversationKey = `${message.postId}-${participantId}`;
          
          if (!conversationMap.has(conversationKey)) {
            const participant = getUserById(participantId);
            const post = mockPosts.find(p => p.id === message.postId);
            
            if (participant && post) {
              conversationMap.set(conversationKey, {
                id: conversationKey,
                participantId,
                participant,
                postId: message.postId,
                post,
                lastMessage: message,
                unreadCount: isCurrentUserSender ? 0 : (!message.isRead ? 1 : 0),
                messages: [message],
              });
            }
          } else {
            const conversation = conversationMap.get(conversationKey)!;
            conversation.messages.push(message);
            
            // Update last message if this one is newer
            if (new Date(message.timestamp) > new Date(conversation.lastMessage.timestamp)) {
              conversation.lastMessage = message;
            }
            
            // Update unread count
            if (!isCurrentUserSender && !message.isRead) {
              conversation.unreadCount++;
            }
          }
        });

        // Sort conversations by last message timestamp
        const sortedConversations = Array.from(conversationMap.values())
          .sort((a, b) => new Date(b.lastMessage.timestamp).getTime() - new Date(a.lastMessage.timestamp).getTime());

        setConversations(sortedConversations);
        
        // Select first conversation if available
        if (sortedConversations.length > 0) {
          setSelectedConversation(sortedConversations[0]);
        }

        setError(null);
      } catch (err) {
        setError('Failed to load conversations');
      } finally {
        setLoading(false);
      }
    };

    loadConversations();
  }, [currentUser.id]);

  const handleSendMessage = (message: string) => {
    if (!selectedConversation) return;

    // Create new message
    const newMessage: Message = {
      id: Math.random().toString(36).substr(2, 9),
      postId: selectedConversation.postId,
      senderId: currentUser.id,
      receiverId: selectedConversation.participantId,
      content: message,
      timestamp: new Date().toISOString(),
      isRead: false,
    };

    // Update the selected conversation
    const updatedConversation = {
      ...selectedConversation,
      lastMessage: newMessage,
      messages: [...selectedConversation.messages, newMessage],
    };

    setSelectedConversation(updatedConversation);

    // Update conversations list
    setConversations(prev => 
      prev.map(conv => 
        conv.id === selectedConversation.id 
          ? updatedConversation
          : conv
      ).sort((a, b) => new Date(b.lastMessage.timestamp).getTime() - new Date(a.lastMessage.timestamp).getTime())
    );

    toast({
      title: 'Message sent!',
      description: 'Your message has been delivered.',
      status: 'success',
      duration: 2000,
    });
  };

  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    
    // Mark messages as read
    if (conversation.unreadCount > 0) {
      const updatedConversation = {
        ...conversation,
        unreadCount: 0,
        messages: conversation.messages.map(msg => 
          msg.receiverId === currentUser.id ? { ...msg, isRead: true } : msg
        ),
      };

      setConversations(prev => 
        prev.map(conv => 
          conv.id === conversation.id ? updatedConversation : conv
        )
      );
      setSelectedConversation(updatedConversation);
    }
  };

  const handleDeleteConversation = (conversationId: string) => {
    setConversations(prev => prev.filter(conv => conv.id !== conversationId));
    
    if (selectedConversation?.id === conversationId) {
      const remainingConversations = conversations.filter(conv => conv.id !== conversationId);
      setSelectedConversation(remainingConversations[0] || null);
    }

    toast({
      title: 'Conversation deleted',
      description: 'The conversation has been removed.',
      status: 'success',
      duration: 2000,
    });
  };

  const handleViewPost = (postId: string) => {
    navigate(`/post/${postId}`);
  };

  const filteredConversations = conversations.filter(conv =>
    conv.participant.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.participant.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.post.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffMs = now.getTime() - messageTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return messageTime.toLocaleDateString();
  };

  if (loading) {
    return (
      <Container maxW="7xl" py={8}>
        <LoadingSpinner text="Loading your messages..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="7xl" py={8}>
        <ErrorDisplay
          type="server"
          message={error}
          onRetry={() => window.location.reload()}
          onGoHome={() => navigate('/')}
        />
      </Container>
    );
  }

  return (
    <Container maxW="7xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="xl" mb={2} color="brand.green.dark">
            Messages
          </Heading>
          <Text color="gray.600" fontSize="lg">
            Manage your conversations with buyers and sellers
          </Text>
        </Box>

        {conversations.length === 0 ? (
          <Card>
            <CardBody>
              <VStack spacing={4} py={12} textAlign="center">
                <FaEnvelope size="48px" color="gray.400" />
                <Heading size="md" color="gray.500">
                  No messages yet
                </Heading>
                <Text color="gray.500">
                  When you contact sellers or receive messages from buyers, they'll appear here.
                </Text>
                <Button
                  colorScheme="green"
                  onClick={() => navigate('/browse')}
                >
                  Browse Items
                </Button>
              </VStack>
            </CardBody>
          </Card>
        ) : (
          <Grid templateColumns={{ base: '1fr', lg: '1fr 2fr' }} gap={6} h="600px">
            {/* Conversations List */}
            <GridItem>
              <Card h="full">
                <CardBody>
                  <VStack spacing={4} align="stretch">
                    {/* Search */}
                    <InputGroup>
                      <InputLeftElement>
                        <FaSearch color="gray.400" />
                      </InputLeftElement>
                      <Input
                        placeholder="Search conversations..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>

                    <Divider />

                    {/* Conversations */}
                    <VStack spacing={2} align="stretch" overflowY="auto" flex={1}>
                      {filteredConversations.map((conversation) => (
                        <Card
                          key={conversation.id}
                          variant={selectedConversation?.id === conversation.id ? 'filled' : 'outline'}
                          cursor="pointer"
                          _hover={{ bg: 'gray.50' }}
                          onClick={() => handleSelectConversation(conversation)}
                        >
                          <CardBody p={3}>
                            <Flex align="center" justify="space-between">
                              <HStack spacing={3} flex={1}>
                                <Avatar
                                  size="md"
                                  name={`${conversation.participant.firstName} ${conversation.participant.lastName}`}
                                  src={`https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face`}
                                />
                                <VStack align="start" spacing={1} flex={1} minW={0}>
                                  <HStack spacing={2} w="full">
                                    <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                                      {conversation.participant.firstName} {conversation.participant.lastName}
                                    </Text>
                                    {conversation.unreadCount > 0 && (
                                      <Badge colorScheme="red" borderRadius="full" size="sm">
                                        {conversation.unreadCount}
                                      </Badge>
                                    )}
                                  </HStack>
                                  <Text fontSize="xs" color="gray.600" noOfLines={1}>
                                    {conversation.post.title}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                    {conversation.lastMessage.content}
                                  </Text>
                                </VStack>
                              </HStack>

                              <VStack align="end" spacing={1} flexShrink={0}>
                                <Text fontSize="xs" color="gray.500">
                                  {formatTimeAgo(conversation.lastMessage.timestamp)}
                                </Text>
                                <Menu>
                                  <MenuButton
                                    as={IconButton}
                                    icon={<FaEllipsisV />}
                                    size="xs"
                                    variant="ghost"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <MenuList>
                                    <MenuItem
                                      icon={<FaUserCircle />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewPost(conversation.postId);
                                      }}
                                    >
                                      View Post
                                    </MenuItem>
                                    <MenuItem
                                      icon={<FaTrash />}
                                      color="red.500"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteConversation(conversation.id);
                                      }}
                                    >
                                      Delete
                                    </MenuItem>
                                  </MenuList>
                                </Menu>
                              </VStack>
                            </Flex>
                          </CardBody>
                        </Card>
                      ))}
                    </VStack>
                  </VStack>
                </CardBody>
              </Card>
            </GridItem>

            {/* Message Interface */}
            <GridItem>
              {selectedConversation ? (
                <Card h="full">
                  <CardBody>
                    <VStack spacing={4} align="stretch" h="full">
                      {/* Conversation Header */}
                      <HStack spacing={3} pb={2} borderBottom="1px solid" borderColor="gray.200">
                        <Avatar
                          size="sm"
                          name={`${selectedConversation.participant.firstName} ${selectedConversation.participant.lastName}`}
                          src={`https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face`}
                        />
                        <VStack align="start" spacing={0} flex={1}>
                          <Text fontWeight="semibold">
                            {selectedConversation.participant.firstName} {selectedConversation.participant.lastName}
                          </Text>
                          <Text fontSize="sm" color="gray.600" noOfLines={1}>
                            About: {selectedConversation.post.title}
                          </Text>
                        </VStack>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewPost(selectedConversation.postId)}
                        >
                          View Post
                        </Button>
                      </HStack>

                      {/* Messages */}
                      <Box flex={1}>
                        <MessageInterface
                          key={selectedConversation.id}
                          postId={selectedConversation.postId || ''}
                          currentUserId={currentUser.id}
                          otherUserId={selectedConversation.participantId}
                          onSendMessage={handleSendMessage}
                        />
                      </Box>
                    </VStack>
                  </CardBody>
                </Card>
              ) : (
                <Card h="full">
                  <CardBody>
                    <Flex align="center" justify="center" h="full">
                      <VStack spacing={4} textAlign="center">
                        <FaEnvelope size="48px" color="gray.400" />
                        <Text color="gray.500">
                          Select a conversation to start messaging
                        </Text>
                      </VStack>
                    </Flex>
                  </CardBody>
                </Card>
              )}
            </GridItem>
          </Grid>
        )}
      </VStack>
    </Container>
  );
};

export default MessagesPage; 